import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import URLs from "constants/urls";
import { useNavigate } from "react-router-dom";
import {
  TableContainer,
  Paper,
  Box,
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
  ViewButton,
} from "components/StyledComponents/styledComponents.js";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx"; // Import XLSX for Excel export
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline"; // Download icon for the button
import { monthEndStyles } from "components/Styles/monthEndStyles";
import Cookie from "js-cookie";
import { Height } from "@mui/icons-material";
export default function InsurancePayment(props) {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const jwt_token = Cookie.get("jwt_token");
  const [openConfirmBox, setOpenConfirmBox] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [alertType, setAlertType] = useState("success");
  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return "₹ 0.00";
    return `₹ ${parseFloat(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date)) return "";

    const options = { year: "numeric", month: "short", day: "2-digit" };
    let formattedDate = date.toLocaleDateString("en-GB", options);

    // Remove the comma
    formattedDate = formattedDate.replace(",", "");

    const [day, month, year] = formattedDate.split(" ");
    if (!day || !month || !year) return "";

    const pascalMonth = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    return `${day}-${pascalMonth}-${year}`;
  };
  const columnDefs = [
    {
      headerName: "Renewal Date",
      field: "renewalDate",
      maxWidth: 130,
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Customer Name",
      field: "customerName",
      minWidth: 150,
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Application Number",
      field: "applicationNumber",
      minWidth: 160,
      flex: 1,
    },
    {
      headerName: "Provider",
      field: "providerName",
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "Premium Amount",
      field: "premiumAmount",
      minWidth: 150,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "700" },
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      headerName: "Agent Name",
      field: "agentName",
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "Remarks",
      field: "remarks",
      minWidth: 200,
      editable: true,
      flex: 1,
    },
    {
      headerName: "Action",
      field: "Submit",
      width: 100,
      cellRenderer: (params) => (
        <StyledButton
          variant="contained"
          color="primary"
          size="small"
          type="button"
          sx={{ height: "30px" }}
          onClick={() => handleOpenDialog(params.data)}
        >
          Submit
        </StyledButton>
      ),
    },
  ];
  const handleOpenDialog = (data) => {
    setConfirmData(data);
    setOpenConfirmBox(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmBox(false);
    setConfirmData(null);
  };
  const handleConfirmPayment = async () => {
    const url = `${URLs.updateInsurancePaidStatus}?entryId=${confirmData.entryId}&scheduleDate=${confirmData.renewalDate}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify({
          remarks: confirmData.remarks,
        }),
      });

      if (response.ok) {
        setAlert("Payment confirmed successfully", "success");
        setConfirmData(null);
        fetchInsuranceEntries();
      } else {
        setAlert(`Failed to Confirm Payment`, "error");
      }
    } catch (error) {
      setAlert("An error occurred while confirming payment", "error");
    } finally {
      handleCloseDialog();
    }
  };
  const fetchInsuranceEntries = async () => {
    setLoading(true);
    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      };
      const response = await fetch(`${URLs.insuranceMainURl}?paidStatus=N&payoutStatus=N`, options);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setRowData(data); // Set the fetched data in rowData
    } catch (error) {
      console.error("Error fetching data:", error);
      setAlert("Error fetching data!", "error");
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchInsuranceEntries();
  }, []);
  return (
    <DashboardLayout>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <Dialog open={openConfirmBox} onClose={handleCloseDialog}>
        <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
          <DialogContentText sx={dialogContentTextStyle}>
            Are you sure you want to confirm payment of ₹
            {Number(confirmData?.premiumAmount).toLocaleString("en-IN")}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseDialog} color="secondary">
            Cancel
          </StyledButton>
          <StyledButton onClick={handleConfirmPayment} color="primary">
            Confirm
          </StyledButton>
        </DialogActions>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <StyledAgGridContainer component={Paper} className={`ag-theme-alpine`}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={10}
          />
        </StyledAgGridContainer>
      </TableContainer>
    </DashboardLayout>
  );
}
